/**
 * Locale switcher as a segmented flag-tab strip (TM / RU / EN), flag-only — no text.
 *
 * A tab strip instead of the old select-only combobox: the three languages sit side by side,
 * each its own tab, so the switch is one click with no popover. Flags are tiny inline SVGs
 * (emoji flags render as bare "RU"/"TM" letter pairs on Windows), simplified for legibility at
 * 20px — see the note on `Flag`.
 *
 * Semantics: a radiogroup, not a tablist. These buttons change a preference, they do not reveal
 * associated panels, so `role=radio` (+ `aria-checked`) is the honest mapping. Roving tabindex
 * keeps the group a single tab-stop; arrow keys move (and select) between flags.
 */
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/components/ui';
import { LOCALES, LOCALE_LABELS } from '@/i18n';
import type { Locale } from '@/shared/contract';

function Flag({ locale }: { locale: Locale }) {
  const shared = 'h-3.5 w-5 shrink-0 rounded-[3px] ring-1 ring-black/10';
  switch (locale) {
    case 'ru':
      return (
        <svg viewBox="0 0 24 16" className={shared} aria-hidden>
          <rect width="24" height="16" fill="#fff" />
          <rect y="5.33" width="24" height="5.34" fill="#0039a6" />
          <rect y="10.67" width="24" height="5.33" fill="#d52b1e" />
        </svg>
      );
    case 'en':
      return (
        <svg viewBox="0 0 24 16" className={shared} aria-hidden>
          <rect width="24" height="16" fill="#012169" />
          <path d="M0 0l24 16M24 0L0 16" stroke="#fff" strokeWidth="3.2" />
          <path d="M0 0l24 16M24 0L0 16" stroke="#c8102e" strokeWidth="1.4" />
          <path d="M12 0v16M0 8h24" stroke="#fff" strokeWidth="5.2" />
          <path d="M12 0v16M0 8h24" stroke="#c8102e" strokeWidth="3" />
        </svg>
      );
    case 'tk':
      return (
        <svg viewBox="0 0 24 16" className={shared} aria-hidden>
          <rect width="24" height="16" fill="#00843d" />
          <rect x="2.5" width="3.5" height="16" fill="#b01e33" />
          <circle cx="11.5" cy="5" r="2.7" fill="#fff" />
          <circle cx="12.5" cy="4.4" r="2.4" fill="#00843d" />
          {/* The five stars, as dots — real five-pointed stars are sub-pixel noise at this size. */}
          <circle cx="15" cy="2.4" r="0.6" fill="#fff" />
          <circle cx="16.6" cy="3.6" r="0.6" fill="#fff" />
          <circle cx="17" cy="5.6" r="0.6" fill="#fff" />
          <circle cx="15.8" cy="7.3" r="0.6" fill="#fff" />
          <circle cx="13.9" cy="7.6" r="0.6" fill="#fff" />
        </svg>
      );
  }
}

export function LocaleTabs({
  value,
  onChange,
  className,
}: {
  value: Locale;
  onChange: (locale: Locale) => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  const move = (to: number) => {
    const idx = ((to % LOCALES.length) + LOCALES.length) % LOCALES.length;
    const next = LOCALES[idx];
    refs.current[idx]?.focus();
    if (next !== value) onChange(next);
  };

  const onKeyDown = (e: React.KeyboardEvent, i: number) => {
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        move(i + 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        move(i - 1);
        break;
      case 'Home':
        e.preventDefault();
        move(0);
        break;
      case 'End':
        e.preventDefault();
        move(LOCALES.length - 1);
        break;
    }
  };

  return (
    <div
      role="radiogroup"
      aria-label={t('common.language')}
      className={cn('inline-flex items-center gap-0.5 rounded-lg bg-muted/60 p-0.5', className)}
    >
      {LOCALES.map((locale, i) => {
        const selected = locale === value;
        return (
          <button
            key={locale}
            ref={(el) => (refs.current[i] = el)}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={LOCALE_LABELS[locale]}
            title={LOCALE_LABELS[locale]}
            tabIndex={selected ? 0 : -1}
            onClick={() => locale !== value && onChange(locale)}
            onKeyDown={(e) => onKeyDown(e, i)}
            className={cn(
              'flex h-7 w-8 items-center justify-center rounded-md transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-app',
              selected
                ? 'bg-card shadow-sm ring-1 ring-black/5'
                : 'opacity-60 hover:opacity-100 hover:bg-card/50',
            )}
          >
            <Flag locale={locale} />
          </button>
        );
      })}
    </div>
  );
}
